import React, { useEffect, useRef, useState } from 'react';
import Slider from 'react-rangeslider';
import 'react-rangeslider/lib/index.css';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import Loading from '../Shared/Loader';
import If from '../If';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getBestSellerProduct } from '../../../redux/actions/customizer';
import VideoWithFallback from '../VideoWithFallback';
import { getFontOptions, getSelectedOption } from '../../../utils/commonRingUtils';
import { APP_STYLES, DEFAULT_STYLES } from '../../../utils/basic';
import { useRingCustomizerContext } from '../../context/RingCustomizerContext';
import Accordion from '../Accordion';
import KayJewelersLogo from '../../../assets/images/kay-logo.svg';
import KayVideo from '../../../assets/images/5-ring-video.mp4';
import KayEngravingFilter from './components/KayEngravingFilter';
import KaySizeFilter from './components/KaySizeFilter';
// import BottomArrow from '../../../assets/images/bottom-arrow.png';
import PlusIcon from '../../../assets/images/plus.png';
import CloseIcon from '../../../assets/images/close-icon.svg';

import './index.scss';

type IProps = {
  id: string;
  handleBestSeller?: (id: string) => void;
  engraving: string;
  setEngraving: (text: string) => void;
  size: number;
  setSize: (size: number) => void;
  handleBestSellerDone: () => void;
  product: any;
  navigateToUrl: (url: string, replace?: boolean, bestSeller?: string) => void;
  onClose: () => void;
};

let timedOutId: NodeJS.Timeout | undefined;

const KayDiscountedPopup = ({
  id,
  handleBestSeller,
  product,
  navigateToUrl,
  engraving,
  setEngraving,
  size,
  setSize,
  handleBestSellerDone,
  onClose
}: IProps) => {
  const { subpath } = useRingCustomizerContext();
  const { bestSellerProduct, isFetching } = useAppSelector((state) => state.customizer);
  const dispatch = useAppDispatch();
  const [isEngravingOpen, setIsEngravingOpen] = useState(false);
  const [isSizeChanged, setIsSizeChanged] = useState(false);

  useEffect(() => {
    if (!bestSellerProduct || bestSellerProduct?.bestseller_id !== id) {
      fetchBestSellerProduct({ size: size || 10, engraving: engraving || '' });
    }
  }, [id]);

  const fetchBestSellerProduct = async (data?: { size: number; engraving: string }) => {
    const { size, engraving } = data || {};
    try {
      const payload = {
        id,
        data: {
          size: size || '',
          engraving: engraving || '',
          subpath,
        }
      };
      await dispatch(getBestSellerProduct(payload)).unwrap();
    } catch (error) {
      console.log(error);
    }
  };

  const handleClick = (id: string) => () => {
    navigateToUrl(bestSellerProduct?.ring_options_url, false, id);
  };

  const handleOnChange = (updated: any) => {
    if (updated.engraving_new?.includes('None')) {
      setEngraving('');
      fetchBestSellerProduct({ size: size, engraving: '' });
    } else {
      setEngraving(updated.engraving_new);
      fetchBestSellerProduct({ size: size, engraving: updated.engraving_new });
    }
  };

  const handleSliderChange = (value: number) => {
    setSize(value);
    setIsSizeChanged(true);
    if (timedOutId) {
      clearTimeout(timedOutId);
    }
    timedOutId = setTimeout(() => {
      fetchBestSellerProduct({ size: value, engraving: engraving });
    }, 200);
  };

  const getEngravingContent = () => {
    const fonts = getFontOptions(
      bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []
    ).map((font: any) => ({
      ...font,
      additionalClassName: ''
    }));
    const content = {
      hasSwatch: false,
      fieldType: 'engraving_new',
      options: getFontOptions(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || [], 'kay-engraving-font'
      ),
      selectedOption: getSelectedOption(
        bestSellerProduct?.engraving?.fonts || bestSellerProduct?.engraving_new?.text?.fonts || []
      ),
      engravingText: bestSellerProduct?.engraving_new?.text?.text || ''
    };
    return (
      <div className={clsx("tw-w-full tw-bg-white", { 'tw-hidden': !isEngravingOpen, 'tw-mt-4': isEngravingOpen })}>
        <KayEngravingFilter onChange={handleOnChange} isFetching={false} content={content} />
      </div>
    );
  };

  const styles = APP_STYLES[subpath] || DEFAULT_STYLES;
  const showMagicNumberAtDiscountPopup = styles.showMagicNumberAtDiscountPopup;
  const isEngravingEnabled = bestSellerProduct?.is_engraving_enabled;
  return (
    <div className="xl:tw-container xl:tw-px-10 tw-px-6 tw-mx-auto lg:tw-py-8 tw-py-4 tw-h-full">
      <If condition={!!bestSellerProduct}>
        <div className="kay-header">
          <img src={KayJewelersLogo} width={130}/>
          <div>
            <div className="tw-grid tw-grid-cols-2 tw-text-sm">
              <span>Magic Number #:</span>
              <span className="tw-text-right tw-pl-2 md:tw-pl-4">{ isSizeChanged ? bestSellerProduct?.magic_number : 'Select Finger Size' }</span>
            </div>
          </div>
        </div>
        <div className="kay-popup-content">
          <div className="left-content">
            <div className='xl:tw-w-full lg:tw-w-1/2 md:tw-w-3/4 tw-w-full tw-mx-auto'>
              <div className="image-container">
                <img
                  src={`${process.env.REACT_APP_ASSET_URL}${bestSellerProduct?.images[0]}`}
                  alt="ring"
                  className="tw-object-cover"
                />
              </div>
              {/* <video src={KayVideo} autoPlay={true} loop={true} muted={true} playsInline={true} className="tw-object-cover tw-my-6" /> */}
              <div className="kay-image-sequence tw-flex tw-flex-row tw-justify-center tw-items-center tw-my-6 tw-overflow-x-auto">
                {bestSellerProduct?.image_urls_for_animation?.slice(0, 5).map((i: string, index: number) => (
                  <img
                    key={`animation-${bestSellerProduct?.bestseller_id}-${index}`}
                    src={`${process.env.REACT_APP_ASSET_URL}${i}`}
                    alt="ring"
                    className="kay-animated-image tw-object-cover tw-relative"
                    style={{
                      marginLeft: index > 0 ? '-20px' : '0',
                      zIndex: 1 + index,
                    }}
                  />
                ))}
              </div>
              <button type="button" className="tw-w-full tw-text-center tw-text-sm tw-font-bold tw-py-3 tw-bg-white tw-text-black tw-border tw-border-solid tw-border-black " onClick={onClose}>
                FULLY CUSTOMIZE
              </button>
            </div>
          </div>
          <div className="right-content tw-min-w-[2px]">
            <div className="tw-text-3xl tw-font-bold tw-pb-2">{bestSellerProduct?.price}</div>
            <div className="tw-text-base">{bestSellerProduct?.description}</div>
            <div className="divider"></div>

            <If condition={bestSellerProduct?.variants?.length > 1}>
              <h4 className="tw-text-base tw-font-bold tw-py-4">Weight</h4>
              <div className="tw-flex tw-flex-row tw-flex-wrap tw-gap-3">
                {bestSellerProduct?.variants?.map((variant: any) => (
                  <div
                    key={variant.bestseller_id}
                    onClick={!variant.selected ? handleClick(variant.bestseller_id) : undefined}
                    className={clsx('tw-text-sm kay-weight', {
                      'light-border': !variant.selected,
                      'dark-border': variant.selected
                    })}
                  >
                    {variant.name}
                  </div>
                ))}
              </div>
              <div className="divider"></div>
            </If>


            <h4 className="tw-text-base tw-font-bold tw-py-4">Finger Size:</h4>
            <KaySizeFilter options={bestSellerProduct?.finger_size || bestSellerProduct?.size || []}
              size={size} onChange={handleSliderChange} isSizeChanged={isSizeChanged} />

            <div className="divider"></div>

            <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-pt-4">
              <h4 className="tw-text-base tw-font-bold">ADD AN ENGRAVING</h4>
              <img src={isEngravingOpen ? CloseIcon : PlusIcon} alt="bottom-arrow" className="tw-w-4 tw-cursor-pointer" onClick={() => setIsEngravingOpen(!isEngravingOpen)} />
            </div>
            {getEngravingContent()}
            <div className="divider"></div>
          </div>
        </div>
      </If>
      <Loading timedOut={isFetching} />
    </div>
  );
};

export default KayDiscountedPopup;